/**
 * dimensionTextMirroredXData — XDATA round-trip for Dimension.textMirrored.
 *
 * Issue #565 — user-escalated blocking requirement for PR #558. Full
 * writeup, ruled-out alternatives (anonymous-block regeneration — a much
 * larger, not-MIRRTEXT-specific subsystem this codebase does not have —
 * and repurposing DIMENSION's own OCS extrusion sign, which would risk the
 * same representational ambiguity already found for MLeader's in-plane
 * case, issue #564) at https://github.com/cosc/Edit-dwg/issues/565.
 *
 * AcDbDimension has no native DXF group for MIRRTEXT-driven glyph
 * handedness: dimension text is AutoCAD-regenerated from DIMSTYLE +
 * measurement at render time, unlike TEXT/ATTRIB/ATTDEF, which carry their
 * own literal group-71 text-generation-flags bit. This module follows the
 * SAME established pattern this codebase already uses for other
 * app-private, non-natively-representable per-entity state —
 * `viewportOverrides.ts`'s "EDIT_DWG_VP_OVERRIDES" XDATA marker is the
 * direct precedent this mirrors, including the by-construction APPID-table
 * registration (`packages/dxf-io/src/writer.ts`'s `ensureCustomXDataAppids`
 * — derived from the ACTUAL emission sites, so a brand-new appid here needs
 * no separate registration step).
 *
 * A DEDICATED custom app-id, NOT nested under AutoCAD's own "ACAD" appName
 * the way the pre-existing per-entity DIMVAR-override "DSTYLE" block is
 * (`extractDimensionStyleOverrides`/`buildDimensionStyleOverridesXData`) —
 * this is app-private cosmetic state, not a native AutoCAD DIMVAR override,
 * and must not collide with or be mistaken for one. Coexists peacefully
 * with DSTYLE and any other foreign XDATA on the same entity — this module
 * only ever reads/writes its OWN app-id's entry.
 *
 * Format (a single boolean; no need for the `{}`-grouping the richer
 * viewport block uses for its multi-field structure):
 *   1001  EDIT_DWG_DIM_OVERRIDES   (appid marker; DxfXDataEntry.appName)
 *   1070  1                        (textMirrored — present only when true)
 *
 * Presence-preserving, matching every OTHER field's byte-fidelity
 * convention in this PR and this codebase generally: the marker is
 * emitted ONLY when `textMirrored === true` and omitted entirely (never a
 * literal "0") for the overwhelmingly common unmirrored case — a
 * round-trip through a tool that doesn't understand this marker sees no
 * extra clutter for the default state, matching `textMirrored`'s own
 * `.optional()` schema convention ("absent reads as `false` everywhere").
 */

export const DIM_TEXT_MIRRORED_APPID = "EDIT_DWG_DIM_OVERRIDES";

/** XdataEntry type matching the rest of drawing-model (same shape as
 * `viewportOverrides.ts`'s own local alias — both mirror the dxf-io
 * `DxfXDataEntry` transport type without importing across the package
 * boundary, the same incidental-duplication precedent `dxfBridge
 * .attribExportPosition.ts`'s own header documents for `usesAlignmentPoint`). */
interface XdataEntry {
  appName: string;
  groups: { code: number; value: string }[];
}

/**
 * Read the app-private "is this dimension's text mirrored" marker from a
 * DIMENSION entity's raw XDATA.
 *
 * Returns `true` only when the marker's `1070` group is present and
 * nonzero. A missing/foreign/malformed entry — including a file that has
 * never been touched by this app's own MIRROR command, or one authored
 * entirely in real AutoCAD — returns `false`.
 */
export function readDimensionTextMirrored(
  xdata: readonly XdataEntry[] | undefined,
): boolean {
  if (!xdata || xdata.length === 0) return false;
  const entry = xdata.find((e) => e.appName === DIM_TEXT_MIRRORED_APPID);
  if (!entry) return false;
  return entry.groups.some((g) => g.code === 1070 && g.value !== "0");
}

/**
 * Build (or omit) the app-private "is this dimension's text mirrored" XDATA
 * entry for a DIMENSION entity, given its CURRENT `textMirrored` value.
 *
 * Returns `undefined` for `false`/`undefined` (the common case — no group
 * invented). Callers append the returned entry onto the entity's
 * `xdata` array alongside any other entries (DSTYLE, foreign/vendor
 * blocks) — this function never mutates or inspects existing xdata; it
 * is a pure, presence-preserving projection of the CURRENT typed field,
 * matching how `buildDimensionStyleOverridesXData` re-derives DSTYLE fresh
 * from `styleOverrides` on every export rather than preserving stale bytes.
 */
export function buildDimensionTextMirroredXData(
  textMirrored: boolean | undefined,
): XdataEntry | undefined {
  if (!textMirrored) return undefined;
  return {
    appName: DIM_TEXT_MIRRORED_APPID,
    groups: [{ code: 1070, value: "1" }],
  };
}
